<template>
  <div
    ref="keyboard"
    class="keyboard"
    :class="keyboardClass"
    :style="{ width: width, fontSize: fontSize }"
  >
    <slot v-if="isActiveLangBtn" name="langChange">
      <div ref="langList" :class="specialClass" class="lang_list">
        <div
          v-for="(langCode, idx) in langList"
          :key="idx"
          class="lang_block"
          @click="onLanguageSelected(langCode)"
        >
          <span class="lang_name">{{ langCode }}</span>
          <div class="lang_descr">
            {{ modifiedLayout[langCode].langName || '' }}
          </div>
        </div>
      </div>
    </slot>
    <div
      v-for="(line, i) in modifiedLayout[lang][caseSize]"
      :key="i"
      class="keys__line"
      :style="{ width: getRowWidth(i) }"
    >
      <Button
        v-for="(item, j) in line"
        :key="generateKey(i, j, item)"
        :ref="getRefName(item)"
        :text="item"
        :button-class="buttonClass"
        :special-class="specialClass"
        :active-class="activeClass"
        :lang-list="langList"
        :button-width="buttonWidth"
        v-on="listeners"
        @lang="onChangeLang"
        @caps="onCaps"
        @shift="onShift"
        @input="onInput"
        @bksp="$emit('input', value.slice(0, -1))"
        @space="$emit('input', value + ' ')"
      />
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import {
  DEFAULT_LANG,
  DEFAULT_CAPS,
  DEFAULT_LAYOUT,
  DEFAULT_SHIFT,
} from './consts/settings';
import { regexp } from './helpers/regexp';
import { parseSymbols } from './helpers/parser';
import Button from './Button';

export default {
  name: 'Keyboard',
  components: { Button },
  props: {
    value: { type: String, default: '' },
    layout: { type: Object, default: () => DEFAULT_LAYOUT },
    defaultLanguageCode: { type: String, default: DEFAULT_LANG },
    capsOnInit: { type: Boolean, default: DEFAULT_CAPS },
    shiftOnInit: { type: Boolean, default: DEFAULT_SHIFT },
    fontSize: { type: String, default: '14px' },
    width: { type: String, default: '100%' },
    buttonClass: { type: String, default: 'classic_button' },
    keyboardClass: { type: String, default: 'keyboard' },
    specialClass: { type: String, default: 'special' },
    activeClass: { type: String, default: 'active' },
    rowWidths: { type: Array, default: () => undefined },
  },
  data() {
    return {
      lang: null,
      modifiedLayout: null,
      shift: false,
      caps: false,
      resizeTimeout: null,
      isActiveLangBtn: false,
      buttonWidth: 50,
    };
  },
  computed: {
    langList() {
      return Object.keys(this.modifiedLayout);
    },
    caseSize() {
      if (this.caps || this.shift) return 'shift';
      return 'default';
    },
    langCount() {
      return this.langList.length;
    },
    maxButtons() {
      if (!this.modifiedLayout) return 0;

      return this.modifiedLayout[this.lang][this.caseSize].reduce(
        (acc, val) =>
          acc > val.filter(this.filterSpecials).length ? acc : val.length,
        0,
      );
    },
    listeners() {
      const clone = cloneDeep(this.$listeners);
      delete clone.input;
      return clone;
    },
  },
  watch: {
    layout() {
      this.initLayout();
    },
    maxButtons() {
      this.setButtonSize();
    },
    lang() {
      this.$emit('languageChanged', this.lang);
    },
  },
  beforeMount() {
    this.initLayout();
  },
  mounted() {
    window.addEventListener('resize', this.debounceResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.debounceResize);
  },
  methods: {
    initOptions() {
      this.lang = this.defaultLanguageCode;
      this.caps = this.capsOnInit;
      this.shift = this.shiftOnInit;

      if (!this.layout[this.lang]) {
        this.lang = Object.keys(this.layout)[0];
      }
      this.$nextTick(() => {
        if (this.$refs.caps) {
          this.$refs.caps.forEach((item) => {
            item.$data.isActive = this.caps;
          });
        }
        if (this.$refs.shift) {
          this.$refs.shift.forEach((item) => {
            item.$data.isActive = this.shift;
          });
        }

        //if use number layout - do backspace button same widths with others buttons
        if (!this.shiftOnInit && this.$refs.bksp) {
          this.$refs.bksp.forEach((item) => {
            item.$el.style.flex = 'initial';
          });
        }
        if (!this.shiftOnInit && this.$refs.switchNumberKeyboard) {
          this.$refs.switchNumberKeyboard.forEach((item) => {
            item.$el.style.flex = 'initial';
          });
        }
      });
    },
    initLayout() {
      const layout = {};
      const languages = Object.keys(this.layout);
      const hideLangBtn = !languages.length;
      languages.forEach((lang) => {
        const template = this.layout[lang];
        layout[lang] = {};
        layout[lang].default = parseSymbols(template.default, hideLangBtn);
        layout[lang].shift = parseSymbols(template.shift, hideLangBtn);
        layout[lang].langName = this.layout[lang].langName;
      });
      this.initOptions();
      this.modifiedLayout = layout;
    },
    debounceResize() {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.setButtonSize();
      }, 200);
    },
    onCaps() {
      this.toggleActive('caps');
      !this.caps && this.shift && this.toggleActive('shift');
    },
    onShift() {
      this.toggleActive('shift');
      this.caps && this.toggleActive('caps');
    },
    onInput(buttonType) {
      if (this.shift && !this.caps) this.toggleActive('shift');
      const result = this.value + buttonType;
      this.$emit('input', result);
    },
    onChangeLang(event) {
      if (this.langCount && this.langCount <= 2) {
        this.toggleLang();
        return;
      }

      this.toggleLangActive();
      this.positionSelect(event);
    },
    toggleLangActive() {
      this.isActiveLangBtn = !this.isActiveLangBtn;
      this.$refs.lang.forEach((button) => {
        button.$data.isActive = !button.$data.isActive;
      });
    },
    async positionSelect(event) {
      await this.$nextTick(() => {});
      const langList = this.$refs.langList;
      if (!langList) return;

      const button = event.path.find(({ classList }) =>
        classList.contains('button'),
      );
      if (!button) return;

      const keyboardGap = this.getButtonsGap();
      const keyboardPadding = Number(
        getComputedStyle(this.$el).paddingBottom.slice(0, -2),
      );

      const buttonRect = button.getBoundingClientRect();
      langList.style.bottom = `${
        keyboardPadding + buttonRect.height + keyboardGap
      }px`;
      langList.style.left = `${buttonRect.left - keyboardGap * 2}px`;
    },
    getButtonsGap() {
      const buttons = this.$refs.keyboard.querySelectorAll('.button');
      const buttonMargin = (() => {
        for (const item of buttons) {
          const margin = Number(
            getComputedStyle(item).marginRight.slice(0, -2),
          );
          if (margin) return margin;
        }
      })();
      return buttonMargin || 0;
    },
    toggleLang() {
      this.lang = this.langList.find((lang) => lang !== this.lang);
      this.caps = false;
      this.shift = false;
    },
    onLanguageSelected(langCode) {
      this.lang = langCode;
      this.isActiveLangBtn = false;
    },
    toggleActive(refName) {
      this.$refs[`${refName}`].forEach((button) => {
        button.$data.isActive = !button.$data.isActive;
      });
      this[refName] = !this[refName];
    },
    getRefName(key) {
      return key.match(regexp.findSpecialKeyValue)
        ? key.match(regexp.findSpecialKeyValue)[0]
        : 'button';
    },
    generateKey(i, j, item) {
      if (regexp.findBrackets.test(item)) return `${this.lang}-${i}-${j}`;
      return `${this.lang}-${this.caseSize}-${i}-${j}`;
    },
    setButtonSize() {
      const buttonSize = this.getButtonContainerWidth() / this.maxButtons;
      this.buttonWidth = Math.ceil(buttonSize);
    },
    getButtonContainerWidth() {
      const keyboardGap = this.getButtonsGap();
      const keyboardWidth = Number(
        getComputedStyle(this.$el).width.slice(0, -2),
      );

      const measurement = this.rowWidths
        ? this.rowWidths[0].match(/\d+/g)[0]
        : keyboardWidth;
      return measurement - keyboardGap * (this.maxButtons - 1);
    },
    filterSpecials(string) {
      return !string.match(/{[\s\S]+?\|?[\s\S]+}/g);
    },
    getRowWidth(rowIndex) {
      if (!this.rowWidths || !this.rowWidths[rowIndex]) return '100%';
      return this.rowWidths[rowIndex];
    },
  },
};
</script>

<style lang="scss">
.keyboard {
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px;
  background-color: gray;
  width: 100%;
  .keys__line {
    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }
}
.keys__line {
  display: flex;
  justify-content: center;
  .button {
    &:not(:last-child) {
      margin-right: 14px;
    }
  }
}
.lang_list {
  z-index: 100;
  width: 100%;
  min-width: 100px;
  max-width: 200px;
  position: absolute;
  left: 0;
  cursor: pointer;
  box-sizing: border-box !important;
  border-radius: 6px;
  background-color: #989898;
  color: white;
}
.lang_block {
  padding: 10px;
  display: flex;
  align-items: center;
}
.lang_name {
  margin-right: 10px;
  width: 20px;
}
.lang_block:hover {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
